import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../config';
import './timecards.css';

function isoDate(date) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function defaultRange() {
  const end = new Date();
  const start = new Date();
  start.setDate(end.getDate() - 6);
  return { start_date: isoDate(start), end_date: isoDate(end) };
}

function hoursLabel(value) {
  const amount = Number(value || 0);
  return amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function embedQueryParam(...keys) {
  const hash = window.location.hash || '';
  const hashQuery = hash.includes('?') ? hash.slice(hash.indexOf('?') + 1) : '';
  const params = new URLSearchParams(window.location.search);
  const hashParams = new URLSearchParams(hashQuery);
  for (const key of keys) {
    const value = params.get(key) || hashParams.get(key);
    if (value) return value;
  }
  return '';
}

function embedCompanyId() {
  let value = embedQueryParam(
    'procore.company.id',
    'companyId',
    'company_id',
    'cid',
  );
  if (value.startsWith('{{') && value.endsWith('}}')) {
    value = value.slice(2, -2).trim();
  }
  if (value.toLowerCase() === 'procore.company.id' || value.toLowerCase() === 'procore.company.name') {
    return '';
  }
  return value;
}

function errorText(err) {
  const data = err?.response?.data;
  if (!data) return err?.message || 'Request failed';
  if (typeof data.error === 'string') return data.error;
  if (Array.isArray(data.errors)) return data.errors.map(String).join(', ');
  if (typeof data.message === 'string') return data.message;
  return 'Request failed';
}

function bucketLabel(status) {
  if (status === 'synced') return 'Synced';
  if (status === 'pending_sync') return 'Pending sync';
  return 'Pending approval';
}

export default function TimecardSyncDashboard() {
  const defaults = useMemo(() => defaultRange(), []);
  const [startDate, setStartDate] = useState(defaults.start_date);
  const [endDate, setEndDate] = useState(defaults.end_date);
  const [employeeId, setEmployeeId] = useState('');
  const [projectId, setProjectId] = useState('');
  const [status, setStatus] = useState('');
  const [syncStatus, setSyncStatus] = useState('');
  const [webhookUrl, setWebhookUrl] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [error, setError] = useState('');
  const [flash, setFlash] = useState(null);
  const [selected, setSelected] = useState(() => new Set());

  const loadEntries = useCallback(async (overrides = {}) => {
    setLoading(true);
    setError('');
    setFlash(null);
    const nextSync = overrides.sync_status !== undefined ? overrides.sync_status : syncStatus;
    try {
      const response = await axios.get(`${API_BASE_URL}/api/procore-embed/timecard-entries`, {
        params: {
          start_date: startDate || undefined,
          end_date: endDate || undefined,
          employee_id: employeeId || undefined,
          project_id: projectId || undefined,
          status: status || undefined,
          sync_status: nextSync || undefined,
          company_id: embedCompanyId() || undefined,
        },
      });
      setData(response.data);
      setWebhookUrl((current) => current || response.data?.integration?.webhook_url || '');
      setSelected(new Set());
    } catch (err) {
      setData(null);
      setError(errorText(err));
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate, employeeId, projectId, status, syncStatus]);

  useEffect(() => {
    loadEntries();
    // Initial load only; later loads are driven by Apply filters / Refresh.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const entries = data?.entries || [];
  const summary = data?.summary || { synced: 0, pending_sync: 0, pending_approval: 0, hours: 0, total: 0 };
  const filterOptions = data?.filters || { employees: [], projects: [], statuses: [] };

  const grouped = useMemo(() => {
    const groups = new Map();
    entries.forEach((entry) => {
      const key = String(entry.employee_id || entry.employee_name || 'unknown');
      if (!groups.has(key)) {
        groups.set(key, {
          id: key,
          name: entry.employee_name || 'Unknown employee',
          hours: 0,
          entries: [],
        });
      }
      const group = groups.get(key);
      group.hours += Number(entry.hours || 0);
      group.entries.push(entry);
    });
    return Array.from(groups.values()).sort((a, b) => a.name.localeCompare(b.name));
  }, [entries]);

  const toggleRow = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleGroup = (group) => {
    const ids = group.entries.map((entry) => entry.id);
    setSelected((prev) => {
      const next = new Set(prev);
      const allSelected = ids.every((id) => next.has(id));
      ids.forEach((id) => {
        if (allSelected) next.delete(id);
        else next.add(id);
      });
      return next;
    });
  };

  const selectPendingSync = () => {
    setSelected(new Set(entries.filter((entry) => entry.sync_status === 'pending_sync').map((entry) => entry.id)));
  };

  const sendWebhook = async () => {
    const chosen = entries.filter((entry) => selected.has(entry.id));
    if (!chosen.length) {
      setFlash({ type: 'err', text: 'Select at least one timecard to sync.' });
      return;
    }
    setSyncing(true);
    setFlash(null);
    try {
      const response = await axios.post(`${API_BASE_URL}/api/procore-embed/timecard-entries/sync-webhook`, {
        integration_vuid: data?.integration?.vuid,
        company_id: embedCompanyId() || data?.integration?.company_id,
        webhook_url: webhookUrl,
        entries: chosen,
      });
      const sent = response.data?.sent || chosen.length;
      setFlash({ type: 'ok', text: `Sent ${sent} timecard${sent === 1 ? '' : 's'} to the webhook listener.` });
    } catch (err) {
      setFlash({ type: 'err', text: errorText(err) });
    } finally {
      setSyncing(false);
    }
  };

  return (
    <div className="pe-root">
      <header className="pe-top">
        <div>
          <p className="pe-kicker">Procore company app · POC</p>
          <h1>Timesheet sync</h1>
          <p className="pe-sub">
            Company timecard entries grouped by employee. Synced rows have an origin ID from the external system.
          </p>
        </div>
        <div className="pe-top-meta">
          {data?.integration ? (
            <>
              <span>{data.integration.name}</span>
              <span>Company {data.integration.company_id}</span>
            </>
          ) : null}
        </div>
      </header>

      <section className="pe-cards">
        <button
          type="button"
          className={`pe-card synced ${syncStatus === 'synced' ? 'active' : ''}`}
          onClick={() => {
            const next = syncStatus === 'synced' ? '' : 'synced';
            setSyncStatus(next);
            loadEntries({ sync_status: next });
          }}
        >
          <span>Synced</span>
          <strong>{summary.synced}</strong>
          <em>Has origin ID</em>
        </button>
        <button
          type="button"
          className={`pe-card pending ${syncStatus === 'pending_sync' ? 'active' : ''}`}
          onClick={() => {
            const next = syncStatus === 'pending_sync' ? '' : 'pending_sync';
            setSyncStatus(next);
            loadEntries({ sync_status: next });
          }}
        >
          <span>Pending sync</span>
          <strong>{summary.pending_sync}</strong>
          <em>Approved, no origin ID</em>
        </button>
        <button
          type="button"
          className={`pe-card approval ${syncStatus === 'pending_approval' ? 'active' : ''}`}
          onClick={() => {
            const next = syncStatus === 'pending_approval' ? '' : 'pending_approval';
            setSyncStatus(next);
            loadEntries({ sync_status: next });
          }}
        >
          <span>Pending approval</span>
          <strong>{summary.pending_approval}</strong>
          <em>Not approved yet</em>
        </button>
        <div className="pe-card hours">
          <span>Hours in view</span>
          <strong>{hoursLabel(summary.hours)}</strong>
          <em>{summary.total} entries</em>
        </div>
      </section>

      <form
        className="pe-filters"
        onSubmit={(event) => {
          event.preventDefault();
          loadEntries();
        }}
      >
        <label>
          Start date
          <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
        </label>
        <label>
          End date
          <input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
        </label>
        <label>
          Employee
          <select value={employeeId} onChange={(e) => setEmployeeId(e.target.value)}>
            <option value="">All employees</option>
            {(filterOptions.employees || []).map((row) => (
              <option key={row.id} value={row.id}>{row.name}</option>
            ))}
          </select>
        </label>
        <label>
          Project
          <select value={projectId} onChange={(e) => setProjectId(e.target.value)}>
            <option value="">All projects</option>
            {(filterOptions.projects || []).map((row) => (
              <option key={row.id} value={row.id}>{row.name}</option>
            ))}
          </select>
        </label>
        <label>
          Status
          <select value={status} onChange={(e) => setStatus(e.target.value)}>
            <option value="">All statuses</option>
            {(filterOptions.statuses || []).map((row) => (
              <option key={row} value={row}>{row}</option>
            ))}
          </select>
        </label>
        <label>
          Sync status
          <select value={syncStatus} onChange={(e) => setSyncStatus(e.target.value)}>
            <option value="">All</option>
            <option value="synced">Synced (has origin ID)</option>
            <option value="pending_sync">Pending sync</option>
            <option value="pending_approval">Pending approval</option>
          </select>
        </label>
        <div className="pe-filter-actions">
          <button type="submit" className="btn-claret" disabled={loading}>
            {loading ? 'Loading…' : 'Apply filters'}
          </button>
          <button type="button" className="btn-claret" onClick={loadEntries} disabled={loading}>
            Refresh
          </button>
        </div>
      </form>

      <section className="pe-sync-bar">
        <label className="pe-webhook">
          Webhook listener
          <input
            type="url"
            placeholder="https://example.com/timesheet-listener"
            value={webhookUrl}
            onChange={(e) => setWebhookUrl(e.target.value)}
          />
        </label>
        <button type="button" className="btn-claret" onClick={selectPendingSync} disabled={!entries.length}>
          Select pending sync
        </button>
        <button type="button" className="btn-claret" onClick={sendWebhook} disabled={syncing || selected.size === 0}>
          {syncing ? 'Sending…' : `Sync selected (${selected.size})`}
        </button>
      </section>

      {error ? <div className="pe-banner err">{error}</div> : null}
      {flash ? <div className={`pe-banner ${flash.type}`}>{flash.text}</div> : null}

      {loading && !entries.length ? (
        <div className="pe-empty">Loading timecard entries from Procore…</div>
      ) : grouped.length === 0 ? (
        <div className="pe-empty">No timecard entries match the current filters.</div>
      ) : (
        grouped.map((group) => {
          const allSelected = group.entries.every((entry) => selected.has(entry.id));
          return (
            <section key={group.id} className="pe-group">
              <header className="pe-group-head">
                <label>
                  <input
                    type="checkbox"
                    checked={allSelected}
                    onChange={() => toggleGroup(group)}
                  />
                  <span>{group.name}</span>
                </label>
                <em>{hoursLabel(group.hours)} hrs · {group.entries.length} entries</em>
              </header>
              <div className="pe-table-wrap">
                <table>
                  <thead>
                    <tr>
                      <th></th>
                      <th>Date</th>
                      <th>Project</th>
                      <th>Task code</th>
                      <th>Sub job</th>
                      <th className="num">Hours</th>
                      <th>Status</th>
                      <th>Time type</th>
                      <th>Origin ID</th>
                      <th>Sync</th>
                    </tr>
                  </thead>
                  <tbody>
                    {group.entries.map((entry) => (
                      <tr key={entry.id}>
                        <td>
                          <input
                            type="checkbox"
                            checked={selected.has(entry.id)}
                            onChange={() => toggleRow(entry.id)}
                          />
                        </td>
                        <td>{entry.date || '—'}</td>
                        <td>
                          {entry.project_number ? `${entry.project_number} — ` : ''}
                          {entry.project_name || '—'}
                        </td>
                        <td>{entry.task_code || '—'}</td>
                        <td>{entry.sub_job || '—'}</td>
                        <td className="num">{hoursLabel(entry.hours)}</td>
                        <td>{entry.approval_status || '—'}</td>
                        <td>{entry.time_type || '—'}</td>
                        <td className="mono">{entry.origin_id || '—'}</td>
                        <td>
                          <span className={`pe-pill ${entry.sync_status}`}>{bucketLabel(entry.sync_status)}</span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          );
        })
      )}
    </div>
  );
}
